import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  structuredData?: object;
}

const SEOHead = ({ title, description, canonicalPath, structuredData }: SEOHeadProps) => {
  const location = useLocation();
  const { language } = useLanguage();
  
  const baseUrl = "https://playfieldvr.nl";
  const currentPath = canonicalPath || location.pathname;
  // Remove query params and trailing slashes for canonical
  const cleanPath = currentPath.split('?')[0].replace(/\/$/, '') || '/';
  const canonicalUrl = `${baseUrl}${cleanPath}`;
  
  // Organization / WebSite / FAQPage / Review / Service schemas live once in
  // index.html so they cannot drift or duplicate. Here we only add
  // page-specific breadcrumbs and any schema a page passes in explicitly.


  // Breadcrumb Schema - Generate based on path
  const generateBreadcrumbs = () => {
    const pathSegments = cleanPath.split('/').filter(Boolean);
    if (pathSegments.length === 0) return null;

    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        }
      ]
    };

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      breadcrumbList.itemListElement.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        "item": `${baseUrl}${currentPath}`
      });
    });

    return breadcrumbList;
  };

  const breadcrumbSchema = generateBreadcrumbs();

  // Combine all schemas
  const allSchemas = [
    breadcrumbSchema,
    structuredData
  ].filter(Boolean);
  
  return (
    <Helmet>
      {/* Security Headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
      <meta httpEquiv="Permissions-Policy" content="interest-cohort=()" />
      
      {/* Canonical URL - Clean, no query params or trailing slash */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags for multilingual SEO */}
      <link rel="alternate" hrefLang="nl" href={`${baseUrl}${cleanPath}`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}${cleanPath}?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${cleanPath}`} />
      
      {/* Dynamic Title & Description if provided */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      
      {/* Open Graph dynamic updates */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={language === 'nl' ? 'nl_NL' : 'en_US'} />
      <meta property="og:site_name" content="PlayfieldVR" />
      
      {/* Twitter Card dynamic updates */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      
      {/* Structured Data (JSON-LD) */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
